import React, { useMemo, useState } from "react"

import { AmplitudeEvent } from "@common-shared/config/amplitude.config"
import { PayerCoverageCheckNetwork } from "@common-shared/config/coverage.config"
import { FeatureFlag } from "@common-shared/config/featureFlags.config"
import { PoliciesListType } from "@common-shared/config/oneVerify/oneVerifyPolicies.config"
import { IOneVerifyCoverageCheckHistory } from "@common-shared/types/responseData/oneVerifyResponseData.types"

import { NIRVANA_COLORS } from "@front-shared/config/theme.config"
import { ButtonElement } from "@front-shared/elements/button.element"
import { PrimaryText } from "@front-shared/elements/primaryText.element"
import { TooltipElement } from "@front-shared/elements/tooltip.element"
import { ConfirmationHelper } from "@front-shared/helpers/confirmation.helper"

import { POLICIES_CONFIG } from "../../../config/policies.config"
import { TOOLTIPS_CONFIG } from "../../../config/tooltips.config"
import { AmplitudeHelper } from "../../../helpers/amplitude.helper"
import { RuntimeHelper } from "../../../helpers/runtime.helper"
import { useIsFeatureFlagEnabled } from "../../../hooks/isFeatureFlagEnabled.hook"
import { useWriteToEhr } from "../../../hooks/writeToEhr.hooks"
import { useAppDispatch } from "../../../store/appDispatch.hook"
import { useAppSelector } from "../../../store/selectors/app.selector"
import { runtimeSelector } from "../../../store/selectors/runtime.selector"
import { selectedPracticeRoleSelector } from "../../../store/selectors/selectedPracticeRole.selector"
import { policiesWriteToEhr } from "../../../store/thunks/policies.thunks"
import { ICommonCheckResult } from "../../../types/objects.types"

export const WriteToEhrButtonComponent: React.FunctionComponent<
    IWriteToEhrButtonComponentProps
> = props => {
    const hasFeatureFlag = useIsFeatureFlagEnabled(
        FeatureFlag.ONE_VERIFY_EHR_WRITE_SINGLE,
    )

    const { determineIsWriteToEhrAllowed, isWriteToEhrAllowed, tooltip } =
        useWriteToEhr(props.coverageCheckResult)

    const [isSubmenuOpen, setIsSubmenuOpen] = useState(false)

    const dispatch = useAppDispatch()

    const runtimeState = useAppSelector(runtimeSelector)

    const selectedPracticeRole = useAppSelector(selectedPracticeRoleSelector)

    const isWriteToEhrLoading = useMemo<boolean>(() => {
        return RuntimeHelper.isWriteToEhrLoading()
    }, [runtimeState.isLoading])

    const isWriteToEhrPending = useMemo<boolean>(() => {
        // Technically the INN and OON will have separate policies,
        // but currently when both are present they will have the same patient ID,
        // so best to disable if one is pending
        return (
            (!!props.coverageCheckResult.IN?.policy?.ehrWriteLog?.status &&
                !POLICIES_CONFIG.oneVerifyEhrWriteTerminalStatuses.includes(
                    props.coverageCheckResult.IN.policy.ehrWriteLog.status,
                )) ||
            (!!props.coverageCheckResult.OUT?.policy?.ehrWriteLog?.status &&
                !POLICIES_CONFIG.oneVerifyEhrWriteTerminalStatuses.includes(
                    props.coverageCheckResult.OUT.policy.ehrWriteLog.status,
                ))
        )
    }, [props.coverageCheckResult])

    const onWriteToEhr = (selectedItem: IOneVerifyCoverageCheckHistory) => {
        if (!selectedPracticeRole?.practice) {
            return
        }

        AmplitudeHelper.logEvent(AmplitudeEvent.EHR_WRITE_BUTTON_CLICKED, {
            practiceId: selectedPracticeRole.practice.id,
            practiceName: selectedPracticeRole.practice.name,
        })

        ConfirmationHelper.enableConfirmation(
            () => {
                const nirvanaRequestId = selectedItem.coverageResult.requestId

                if (!nirvanaRequestId) {
                    return
                }

                AmplitudeHelper.logEvent(
                    AmplitudeEvent.EHR_WRITE_MODAL_CONFIRMED,
                    {
                        practiceId: selectedPracticeRole.practice.id,
                        practiceName: selectedPracticeRole.practice.name,
                    },
                )

                dispatch(
                    policiesWriteToEhr({
                        practice: selectedPracticeRole.practice,
                        payload: {
                            nirvanaRequestId,
                            listType: props.listType,
                            // This will be the nirvanaRequestId on the single policy view,
                            // but the resultId (originating from the frontend form) for the coverage checker manual result view.
                            // We're using it to find the item in redux store to update it when done
                            oneVerifyInternalId:
                                selectedItem.coverageResult.resultId,
                        },
                        onSuccess: props.onSuccess,
                    }),
                )
            },
            "Confirm write to EHR",
            <>
                Please confirm that you would like to write verification details
                to your EHR using the EHR patient ID{" "}
                <PrimaryText className="inline" typography="textSemibold">
                    {selectedItem.coverageResult.customerPatientId}
                </PrimaryText>
            </>,
            () => {
                AmplitudeHelper.logEvent(
                    AmplitudeEvent.EHR_WRITE_MODAL_CANCELED,
                    {
                        practiceId: selectedPracticeRole.practice.id,
                        practiceName: selectedPracticeRole.practice.name,
                    },
                )
            },
            "Confirm",
            "Never mind",
        )
    }

    const handleWriteButtonClick = () => {
        // When the Write to EHR button is clicked...
        if (isSubmenuOpen) {
            // If the submenu is open, close it and return
            setIsSubmenuOpen(false)

            return
        } else if (
            props.coverageCheckResult.IN &&
            props.coverageCheckResult.OUT
        ) {
            // If there are both IN and OUT results, open the submenu and return; onWriteToEhr will be handled in the submenu
            setIsSubmenuOpen(true)

            return
        } else if (props.coverageCheckResult.IN) {
            // If there is only an IN result, make it the selected result for writing to EHR
            onWriteToEhr(props.coverageCheckResult.IN)
        } else if (props.coverageCheckResult.OUT) {
            // If there is only an IN result, make it the selected result for writing to EHR
            onWriteToEhr(props.coverageCheckResult.OUT)
        }
        // Shouldn't reach this point; there should always be an IN or OUT result
    }

    if (
        !hasFeatureFlag ||
        (props.listType &&
            POLICIES_CONFIG.ehrWriteDisabledListTypes.includes(props.listType))
    ) {
        return null
    }

    return (
        <div className={`relative ${props.className}`}>
            <ButtonElement
                label={
                    isWriteToEhrLoading || isWriteToEhrPending
                        ? "Writing..."
                        : "Write to EHR"
                }
                type="primary"
                disabled={
                    // Including isWriteToEhrLoading to get disabled styles while loading
                    !isWriteToEhrAllowed ||
                    isWriteToEhrLoading ||
                    isWriteToEhrPending
                }
                onClick={handleWriteButtonClick}
                isLoading={isWriteToEhrLoading || isWriteToEhrPending}
            />

            {isSubmenuOpen && (
                // Submenu where user can select which check (INN or OON) to write to EHR if both are present
                // Includes logic individual to each option to determine if it can be written to EHR
                <div
                    className="
                        absolute w-[200px] z-100 bg-brand-white top-[50px] right-0
                        rounded-[8px] border border-solid border-brand-warmShadow
                        shadow-6 overflow-hidden
                    "
                >
                    {Object.keys(props.coverageCheckResult).map(key => {
                        const typedKey = key as keyof ICommonCheckResult

                        const checkResult = props.coverageCheckResult[typedKey]

                        // Should exist, but return null if it doesn't
                        if (!checkResult) {
                            return null
                        }

                        // Pass just this check result, formatted as an ICommonCheckResult object, to determine if it can be written to EHR
                        const { isWriteToEhrAllowed, tooltip } =
                            determineIsWriteToEhrAllowed({
                                [typedKey]: checkResult,
                            })

                        const checkType =
                            typedKey === PayerCoverageCheckNetwork.IN
                                ? "INN"
                                : "OON"

                        return (
                            <button
                                key={key}
                                type="button"
                                disabled={!isWriteToEhrAllowed}
                                onClick={() => {
                                    setIsSubmenuOpen(false)

                                    onWriteToEhr(checkResult)
                                }}
                                className={`w-full flex items-center p-[16px] ${
                                    !isWriteToEhrAllowed
                                        ? "disabled-background cursor-not-allowed"
                                        : undefined
                                }`}
                            >
                                <PrimaryText
                                    typography="buttonText"
                                    className={
                                        !isWriteToEhrAllowed
                                            ? "opacity-70"
                                            : undefined
                                    }
                                >
                                    Write {checkType} Check
                                </PrimaryText>

                                {!isWriteToEhrAllowed && (
                                    <TooltipElement
                                        text={tooltip}
                                        className="relative ml-[7px] -top-[2px]"
                                    />
                                )}
                            </button>
                        )
                    })}
                </div>
            )}

            {!isWriteToEhrAllowed && (
                <TooltipElement
                    inlineLabel={
                        TOOLTIPS_CONFIG.singlePolicyView.cantWriteToEhrText
                            .title
                    }
                    inlineLabelTypography="captionSemibold"
                    isInlined
                    text={tooltip}
                    backgroundColor={NIRVANA_COLORS.brand.black}
                />
            )}
        </div>
    )
}

/**
 * Currently this component serves both the single policy view and the coverage checker manual result view.
 * We convert the coverage history item from the single policy view to a coverageCheckResult prop to standardize the props for both views.
 * It's expected that the single policy view will send a listType prop and the coverage checker manual result view will send an onSuccess prop.
 */
interface IWriteToEhrButtonComponentProps {
    className?: string
    listType?: PoliciesListType
    coverageCheckResult: ICommonCheckResult
    onSuccess?: (result: IOneVerifyCoverageCheckHistory) => void
}
